import { useMemo } from 'react';
import { TableCell, TableFooter, TableRow } from '@mui/material';
import { IGeneralObjectType } from 'src/compass/site/util';

import ICasTableColumn from '../ICasTableColumn';
import CasTableCell from './CasTableCell';
import getCasTableCellValue from '../getCasTableCellValue';

export type CasTableFooterProps = {
  rowData: IGeneralObjectType[];
  totals: IGeneralObjectType;
  columns: ICasTableColumn[];
};

function Footer({ columns, row }: { columns: ICasTableColumn[]; row: IGeneralObjectType }) {
  return (
    <TableFooter>
      <TableRow>
        {columns.map((column, index) => {
          const { field } = column;
          const totalsHasField = field && row?.[field] !== undefined;

          if (totalsHasField) return <CasTableCell key={field} row={row} column={column} />;
          // Spacer under a column that has no total. Keyed on the index because a column
          // without a field has nothing else unique to key on.
          return <TableCell key={`empty-${index}`} />;
        })}
      </TableRow>
    </TableFooter>
  );
}

function calculateTotalsOfColumnValues(dataRows: IGeneralObjectType[], columns: ICasTableColumn[]) {
  return columns.reduce((totals: IGeneralObjectType, column) => {
    if (column.total) {
      const total = dataRows.reduce((sum, row) => {
        const value = parseFloat(getCasTableCellValue({row, column})) || 0; // Handle NaN
        return sum + value;
      }, 0);
      if (column.field) totals[column.field] = total;
    }
    return totals;
  }, {});
}

/* 
    If the first column have total=false, 'TOTAL' label will be displayed in first column.
    If the first column does have total=true, no TOTAL label is displayed, we will display total value.
*/

export default function CasTableFooter({ rowData = [], totals, columns }: CasTableFooterProps) {
  const columnTotals = useMemo(
    () => calculateTotalsOfColumnValues(rowData, columns),
    [rowData, columns]
  );

  const shouldDisplayTotalRow = rowData.length > 1 && columns.some((column) => column.total);

  // totals represent the BE server data.
  if (totals) {
    return <Footer columns={columns} row={totals} />;
  }

  if (shouldDisplayTotalRow) {
    return <Footer columns={columns} row={columnTotals} />;
  }

  return null;
}
